// Liste des dossiers : filtres état, retard, recherche ; flux de progression par statut usager.
import { STATUTS_USAGER, type DossierResume } from '@cc/contrats/ui-metier';
import { Bouton, Champ, EtatVide, Filtres, FluxProgression, Lien, LienBouton, PuceEtat, Tableau, useFiltresPersistes, type ColonneTableau } from '@cc/ui';
import { FolderOpen, Plus } from 'lucide-react';
import { useState } from 'react';
import { EnTetePage } from '../../commun/EnTetePage';
import { EtatRequete } from '../../commun/EtatRequete';
import { date } from '../../commun/format';
import { useSession } from '../../session/FournisseurSession';
import { moduleActif, peut } from '../../session/permissions';
import { libelleEtatDossier, useListePaginee } from './listes';
import commun from '../../commun/commun.module.css';

export function puceDossier(d: Pick<DossierResume, 'etat' | 'en_retard' | 'hors_delai_reglementaire'>) {
  if (d.hors_delai_reglementaire) return <PuceEtat ton="retard" libelle="Hors délai réglementaire" />;
  if (d.en_retard) return <PuceEtat ton="retard" libelle="En retard" />;
  const libelle = libelleEtatDossier(d.etat);
  if (d.etat.startsWith('en_attente_decision')) return <PuceEtat ton="attente" libelle="Décision attendue" />;
  if (d.etat === 'en_signature') return <PuceEtat ton="signer" libelle="À signer" />;
  if (['cloture', 'archive', 'notifie', 'decide'].includes(d.etat)) return <PuceEtat ton="ok" libelle={libelle} />;
  if (d.etat === 'complement_demande') return <PuceEtat ton="attente" libelle={libelle} />;
  return <PuceEtat ton="neutre" libelle={libelle} />;
}

export function PageDossiers() {
  const { contexte } = useSession();
  const [filtres, setFiltres] = useFiltresPersistes('dossiers', { etat: '', retard: '' });
  const [q, setQ] = useState('');
  const [recherche, setRecherche] = useState('');
  const [service, setService] = useState('');
  const liste = useListePaginee<DossierResume>('dossiers', '/dossiers', { etat: filtres.etat ?? '', en_retard: filtres.retard ?? '', q: recherche, service });

  const flux = STATUTS_USAGER.map((s) => ({ libelle: s, valeur: liste.elements.filter((d) => d.statut_usager_libelle === s).length }));
  const colonnes: ColonneTableau<DossierResume>[] = [
    { id: 'numero', entete: 'Numéro', enTeteLigne: true, masquable: false, valeurTri: (d) => d.numero, cellule: (d) => <Lien href={`/dossiers/${d.id}`} className={commun.reference}>{d.numero ?? 'sans numéro'}</Lien> },
    { id: 'demarche', entete: 'Démarche', cellule: (d) => d.demarche.libelle, valeurTri: (d) => d.demarche.libelle },
    { id: 'demandeur', entete: 'Demandeur', cellule: (d) => d.demandeur.nom ?? 'non renseigné' },
    { id: 'service', entete: 'Service', cellule: (d) => d.service?.nom ?? 'non affecté' },
    { id: 'depot', entete: 'Enregistré le', cellule: (d) => date(d.enregistre_le), valeurTri: (d) => d.enregistre_le ?? null },
    { id: 'echeance', entete: 'Échéance interne', cellule: (d) => date(d.echeance_interne), valeurTri: (d) => d.echeance_interne ?? null },
    { id: 'etat', entete: 'État', cellule: puceDossier, valeurTri: (d) => d.etat },
  ];

  return (
    <>
      <EnTetePage
        etiquette="Administration générale"
        titre="Dossiers"
        description="Dossiers de démarches déposés au guichet ou en ligne."
        actions={peut(contexte, 'dossier', 'creer') && moduleActif(contexte, 'M05') !== false ? <LienBouton href="/dossiers/nouveau" variante="principal" icone={Plus}>Dépôt au guichet</LienBouton> : null}
      />
      <Filtres
        groupes={[
          {
            id: 'etat',
            libelle: 'État',
            options: [
              { valeur: '', libelle: 'Tous' },
              { valeur: 'en_verification', libelle: 'En vérification' },
              { valeur: 'complement_demande', libelle: 'Incomplets' },
              { valeur: 'en_instruction', libelle: 'En instruction' },
              { valeur: 'en_attente_decision', libelle: 'Décision attendue' },
              { valeur: 'cloture', libelle: 'Clôturés' },
            ],
          },
          { id: 'retard', libelle: 'Délais', options: [{ valeur: '', libelle: 'Tous' }, { valeur: 'oui', libelle: 'En retard' }] },
        ]}
        valeurs={filtres}
        onChanger={setFiltres}
      />
      <form className={commun.ligne} style={{ alignItems: 'flex-end' }} role="search" onSubmit={(e) => { e.preventDefault(); setRecherche(q.trim()); }}>
        <Champ libelle="Rechercher (numéro, demandeur, contenu)" type="search" value={q} onChange={(e) => setQ(e.target.value)} />
        <Champ libelle="Identifiant du service" value={service} onChange={(e) => setService(e.target.value.trim())} facultatif />
        <Bouton type="submit" variante="secondaire">Rechercher</Bouton>
      </form>
      <EtatRequete chargement={liste.requete.isPending} erreur={liste.requete.error} onReessayer={() => void liste.requete.refetch()} lot="L0-C" fonction="La liste des dossiers">
        {liste.elements.length === 0 ? (
          <EtatVide titre="Aucun dossier" icone={FolderOpen}>
            <p>Aucun dossier ne correspond à ces filtres.</p>
          </EtatVide>
        ) : (
          <>
            <FluxProgression colonnes={flux} titre="Dossiers de la page par statut usager" />
            <Tableau legende="Dossiers" colonnes={colonnes} lignes={liste.elements} cleLigne={(d) => d.id} cleColonnes="dossiers" pagination={liste.pagination} />
          </>
        )}
      </EtatRequete>
    </>
  );
}
